<template>
	<div class="asset-manager">

		<banner class="asset-manager__banner header--additive-padding" :backgrounds="bannerBackgroundImage">

			<theme-btn class="asset-manager__banner-btn" :disabled="!heroGame" @click="downloadHeroGame">{{heroGameBtnTxt}}</theme-btn>

		</banner>

		<div class="panel pt--0--phab">
			<div class="page-container--not-phab">

				<div class="grid grid--s30">
					<div class="grid__item one-quarter--desk one-third--ltab">

						<mq-layout :mq="['mob', 'ptab']">
							<div class="asset-manager__filter-toggle clickable" v-bind:class="{
								'asset-manager__filter-toggle--loading' : !gamesLoaded,
								'asset-manager__filter-toggle--deactive' : !filterIsActive
							}" @click="filterVisible = !filterVisible">
								<div class="page-container">
									<div class="grid grid--nowrap grid--g10 grid--s0 grid--middle">
										<div class="grid__item w--24"></div>
										<div class="grid__item grid__item--auto grid__item--grow">

											<span class="vm fonticon fonticon--list"></span>
											<span class="link--action">Filter Games</span>

										</div>
										<div class="grid__item w--24">
											<span v-if="showFilter" class="fonticon fonticon--close clickable"></span>
										</div>
									</div>
								</div>
							</div>
						</mq-layout>

						<div v-show="showFilter" class="asset-manager__filter" v-bind:class="{
							'asset-manager__filter--loading' : !gamesLoaded,
							'asset-manager__filter--deactive' : !filterIsActive
						}">
							<assets-filter ref="gameFilter" :centralise="$mq === 'mob' || $mq === 'ptab'" :autocompleteData="autocompleteData" :includeSort="false" :includeRegions="false" @filterChanged="filterGames"></assets-filter>
							<div class="mt--24c hc--phab">
								<span class="link--action link--close link--colored clickable" @click="$refs.gameFilter.reset()">
									Clear Filters
								</span>
							</div>

							<mq-layout :mq="['mob', 'ptab']" class="asset-manager__filter-close">
								<theme-btn @click="filterVisible = false">Close Filters</theme-btn>
							</mq-layout>
						</div>

					</div>
					<div class="grid__item three-quarters--desk two-thirds--ltab">

						<div class="page-container--phab">
							<div class="asset-manager__listing">
								<directory-listing
									v-if="gameDirectories && gameDirectories.children && gameDirectories.children.length > 0"
									ref="directoryListing"
									class="asset-manager__directory-listing"
									:directory="gameDirectories"
									:hideItemCountAtRoot="true"
									:preselect="initialFolderId"
									root-name="Asset Manager"
									@navigationChanged="setFilterActivity"
								/>
								<div v-else class="pt--40c--not-phab">
									No games match your chosen filters. <span class="link--action link--colored clickable" @click="$refs.gameFilter.reset()">
										Clear Filters?
									</span>
								</div>
							</div>
						</div>

					</div>
				</div>

			</div>
		</div>

	</div>
</template>

<script>
import { forEach, map, filter, cloneDeep, intersection, orderBy } from 'lodash';

import Btn from './atoms/Btn'
import ThemeBtn from './atoms/ThemeBtn'
import Banner from './molecules/Banner'
import DirectoryListingNav from './molecules/DirectoryListingNav'
import DirectoryListing from './organisms/DirectoryListing'
import AssetsFilter from './organisms/AssetsFilter'

export default {
  name: 'AssetManager',
	components: {
		Btn,
		ThemeBtn,
		Banner,
		DirectoryListingNav,
		DirectoryListing,
		AssetsFilter,
	},
	props: {
		initialFolderId: [Number, String],
	},
  data () {
    return {
			filteredGames: [],
			filterVisible: false,
			filterIsActive: false,
			gamesLoaded: false,
			bannerBackgroundImage: {},
			heroGameDirectoryId: null,
			heroGameBtnTxt: "Download Now",
			heroGame: null,
    }
  },
	computed: {
		showFilter: function()
		{
			var self = this;
			return self.filterVisible || self.$mq === 'ltab' || self.$mq === 'desk' ? true : false;
		},
		autocompleteData: function()
		{
			var self = this;
			if(!self.$root.$data.games) { return []; }

			return map(self.$root.$data.games, function(o)
			{
				return {
					value: o.id,
					search: o.gameName + " " + o.gameDescription,
					label: o.gameName
				};
			});
		},
		gameDirectories: function()
		{
			var self = this;

			var directories = {
				hasChildren: false,
				children: []
			};
			forEach(self.filteredGames, function(game)
			{
				if(game.downloadableAssets)
				{
					directories.children.push(game.downloadableAssets);
					directories.hasChildren = true;
				}
			});

			return directories;
		}
	},
	created: function () {
		var self = this;
		var resolvedCounter = 0;

		self.$root.$data.gamesPromise.then((games) => {

			self.filteredGames = self.$root.$data.games;
			self.gamesLoaded = true;
			self.setHeroGame();

			// We should really check the component hasn't already been destroyed here...
			// self._isDestroyed is an undocumented property though...
			self.$root.$data.appLoading = false;

		}).catch((error) => {
			console.log(error);
		});


    this.getPageContent();
	},
	watch: {
		heroGameDirectoryId() {
			var self = this;

			self.setHeroGame();
		},
  	'$root.userSelectedRegionKey': function()
  	{
			this.$root.appImmediateLoading = true;

      this.getPageContent()
      .then(success =>
      {
				this.$root.appImmediateLoading = false;
      });
  	}
	},
	methods: {
  	getPageContent: function()
  	{
			// Get Hero Data
			var params = this.$root.userSelectedRegion ? {
				regions: [
					this.$root.userSelectedRegion.key,
				],
				fallback: true,
			} : {
				fallback: true,
			};

			return this.$api.getAssetManagerpage(params).then((success) =>
			{
				this.heroData(success.data.content[0]);
			})
			.catch((error) =>
			{
				console.log(error);
			});
  	},
  	heroData(data)
  	{
			var self = this;

			self.bannerBackgroundImage = {
				mob: data.mobileBackgroundImage.link,
				ptab: data.mobileBackgroundImage.link,
				ltab: data.desktopBackgroundImage.link,
				desk: data.desktopBackgroundImage.link,
			};

			self.heroGameDirectoryId = data.cta[0].asset;

			self.heroGameBtnTxt = data.cta[0].text && data.cta[0].text.replace(' ', '').length > 0 ? data.cta[0].text : 'Download Now';
		},
		filterGames(filters)
		{
			var self = this;
			var games = cloneDeep(self.$root.$data.games);

			if(filters.search)
			{
				self.filteredGames = filter(games, {'id': filters.search});
			}
			else
			{
				// Filter game type
				if(Array.isArray(filters.type) && filters.type.length > 0)
				{
					games = filter(games, function(o)
					{
						return intersection(filters.type, o.gameType).length > 0;
					});
				}

				// Filter flags
				if(Array.isArray(filters.flags) && filters.flags.length > 0)
				{
					forEach(filters.flags, function(flag)
					{
						var predicate = {};
						predicate[flag] = true;
						games = filter(games, predicate);
					});
				}

				// Filter volatility
				if(filters.volatility)
				{
					games = filter(games, {'volatilityRating': filters.volatility});
				}

				self.filteredGames = games;
			}

			self.setHeroGame();
		},
		setFilterActivity(navigation)
		{
			var self = this;
			self.filterIsActive = navigation.length <= 1;
		},
		setHeroGame: function()
		{
			var self = this;

			var game = filter(self.filteredGames, function(item){
				return item.id === self.heroGameDirectoryId;
			});

			if(Array.isArray(game) && game.length > 0)
			{
				self.heroGame = game[0].downloadableAssets;
			}
			else
			{
				self.heroGame = null
			}
		},
		downloadHeroGame: function()
		{
			var self = this;
			if(!self.heroGame) { return; }

			self.$refs.directoryListing.openFolder(self.heroGame);
			self.$scrollTo(self.$refs.directoryListing.$el, 500, {
				offset: -40,
			});

			self.trackEvent([
				'banner-button-clicked',
				null,
				self.$root.$data.user ? self.$root.$data.user.trackName : null,
				0,
				false
			]);
		},
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
	@import "src/scss/toolkit";

	$c: ".asset-manager";
	#{$c}
	{
		@include extend(bgc-pal--lightest-grey);

		&__banner
		{
			min-height: 290px;
			@include media(not-phab) { min-height: 400px; }

			&.banner
			{
				@include extend(vertical-bottom);
				padding: {top: rem(40); bottom: rem(40); }
				@include media(not-phab) { padding: {top: rem(56); bottom: rem(56); } }
			}

			.banner__content
			{
				text-align: center;
			}

			&-btn
			{
				&[disabled]
				{
					background-color: palette(grey);
					opacity: 0.9;
				}
			}
		}

		&__filter-toggle
		{
			text-align: center;
			@include extend(bgc-pal--lightest-grey);
			padding: { top: rem(16); bottom: rem(16); }
			transition: opacity 0.4s;
			border-bottom: 1px solid palette('divider');

			&--loading
			{
				position: relative;

				&:before
				{
					content: "";
					display: block;
					@include extend(absolute-fill);
					background-color: transparentize(palette(lightest-grey, base, true), 0.3);
					background-position: center center;
					background-repeat: no-repeat;
					background-image: url(../assets/img/loading.gif);
					z-index: 10;
				}
			}

			&--deactive
			{
				opacity: 0.25;
				pointer-events: none;
			}
		}

		&__filter
		{
			&--loading
			{
				position: relative;

				&:before
				{
					content: "";
					display: block;
					@include extend(absolute-fill);
					background-color: transparentize(palette(lightest-grey, base, true), 0.3);
					background-position: center center;
					background-repeat: no-repeat;
					background-image: url(../assets/img/loading.gif);
					z-index: 10;
				}
			}

			&--deactive
			{
				opacity: 0.25;
				pointer-events: none;
			}

			@include media(not-phab)
			{
				transition: opacity 0.4s;
			}

			@include media(phab)
			{
				padding-top: rem(24);
				padding-bottom: rem(24);
				background-color: palette(white);
				position: absolute;
				width: 100%;
				border-bottom: 1px solid palette(lightest-grey);
				box-shadow: 0 1px 2px rgba(0,0,0,0.2);
				z-index: 100;
			}
		}

		&__filter-close
		{
			text-align: center;
			margin-top: rem(30);
			margin-bottom: rem(16);
		}

		&__listing
		{
			@include extend(bgc-pal--white);
			border: 1px solid palette('divider');

			@include media(phab)
			{
				padding: rem(40) rem(20);
			}

			@include media(not-phab)
			{
				padding: rem(20) rem(30) rem(60) rem(30);
			}
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/AssetManager.vue